import type { CategoriesApi } from "@/v2/api/metadata/categories";
import type { PlayersApi } from "@/v2/api/metadata/players";
import type { SportsApi } from "@/v2/api/metadata/sports";
import type { TeamsApi } from "@/v2/api/metadata/teams";
import type { TournamentsApi } from "@/v2/api/metadata/tournaments";
import type { ContentTab, DialogTabSaveContext } from "@/v2/framework/dialog/types";
import {
  buildSupplementalNamesPatch,
  syncSupplementalNamesBaseline,
} from "@/v2/kits/supplemental-names";
import { createInheritableTabSave } from "@/v2/kits/tenant-inheritance";
import { mergeEntityPatches } from "@/v2/framework/utils/mergeEntityPatches";
import { notify } from "@/v2/notifications/notify";
import { NXGDSStatusVariant } from "@nsftx/seven-nxg-design-system-backoffice";
import { t } from "@/services/i18n";
import { playerStatusOptions } from "../../../../shared";
import {
  buildPlayerAssignmentsPatch,
  hasPlayerAssignment,
  syncPlayerAssignmentsBaseline,
} from "../../assignments";
import { notifySuccess, playerId } from "../shared";

const mainTabChildren: ContentTab["children"] = [
  {
    type: "select-field",
    name: "status",
    label: "Status",
    required: true,
    placeholder: "Select status",
    options: playerStatusOptions,
    inheritance: {
      computedKey: "computedStatus",
      parentKey: "parentStatus",
    },
  },
  {
    type: "text-field",
    name: "name",
    label: "Name",
    placeholder: "Enter name",
    inheritance: {
      computedKey: "computedName",
      parentKey: "parentName",
    },
    validate: (value, values) => {
      if (values.isTenantSourceOfEntity && !String(value ?? "").trim()) {
        return "Required";
      }
      return undefined;
    },
  },
  {
    type: "text-field",
    name: "info",
    label: "Info",
    placeholder: "Enter info",
    inheritance: {
      computedKey: "computedInfo",
      parentKey: "parentInfo",
    },
  },
];

export function createMainTab(options: {
  playersApi: PlayersApi;
  sportsApi: SportsApi;
  categoriesApi: CategoriesApi;
  tournamentsApi: TournamentsApi;
  teamsApi: TeamsApi;
}): ContentTab {
  const { playersApi, sportsApi, categoriesApi, tournamentsApi, teamsApi } = options;

  const save = createInheritableTabSave({
    children: mainTabChildren,
    buildExtraPatch: (ctx) => {
      const merged = mergeEntityPatches(
        buildSupplementalNamesPatch(ctx.values, ctx.baseline),
        buildPlayerAssignmentsPatch(ctx.values, ctx.baseline),
      );
      return Object.keys(merged).length ? merged : null;
    },
    afterPersist: (ctx) => {
      syncSupplementalNamesBaseline(ctx);
      syncPlayerAssignmentsBaseline(ctx);
    },
    persist: async (patch, ctx: DialogTabSaveContext) => {
      if (!hasPlayerAssignment(ctx.values)) {
        notify({
          variant: NXGDSStatusVariant.Negative,
          text: t("metadata.notifications.assign_player_to_team_or_tournament"),
        });
        throw new Error(t("metadata.notifications.assign_player_to_team_or_tournament"));
      }
      await playersApi.update(playerId(ctx.values), patch);
      notifySuccess();
    },
  });

  return {
    id: "main",
    label: "Main",
    save,
    children: [
      ...mainTabChildren,
      {
        type: "component",
        component: () =>
          import("@/v2/kits/supplemental-names/components/SupplementalNamesFields.vue"),
        props: { mode: "edit", entity: "PLAYER" },
      },
      {
        type: "component",
        component: () => import("../../assignments/PlayerAssignmentsFields.vue"),
        props: {
          mode: "edit",
          sportsApi,
          categoriesApi,
          tournamentsApi,
          teamsApi,
        },
      },
    ],
  };
}
